import React, { useEffect, useRef, useCallback } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { clearBackendToken } from '../../utils/backendTokenCache'
import { requestSessionTeardown } from '../../utils/sessionTeardown'
import {
  useRegisterSessionAuth,
  useSessionActivity,
} from '../session/SessionInactivityGuard'

const IS_DEV = process.env.NODE_ENV !== 'production'

// Helper to format milliseconds as readable time
const formatTime = (ms: number) => {
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`
  return `${seconds}s`
}

interface OktaTokenRenewalProps {
  children: React.ReactNode
  // How often to check for token renewal when user is active (in ms)
  renewalCheckInterval?: number
  // How long before token expiry to trigger renewal (in ms)
  renewBeforeExpiry?: number
  // How often to refresh the Okta server session to prevent idle timeout (in ms)
  // Default: 15 minutes (well under Okta's global session idle time of 2 hours)
  sessionRefreshInterval?: number
}

/**
 * Okta token renewal, and the Okta half of session management.
 *
 * Okta's built-in autoRenew only triggers during API calls, so a user active in the app
 * but not making API calls could have their token expire underneath them. This component
 * monitors expiry and renews proactively while the user is active.
 *
 * WHAT IT NO LONGER OWNS (CAPA F273): the HIPAA inactivity warning, countdown dialog,
 * activity listeners and tab-return teardown moved to `SessionInactivityGuard`, which is
 * mounted at the app root outside every auth branch. They lived here, inside a component
 * App.tsx mounts only under `okta.isEnabled && !oidc.isEnabled` — so under the shipped
 * default flags the HIPAA warning could not mount at all, and OIDC deployments never had
 * one. A control measure must not depend on which auth branch is configured.
 *
 * This component now contributes only what is genuinely Okta-specific — how to extend an
 * Okta session and how to end one — by registering a `SessionAuthAdapter`. Idle
 * detection is shared: `useSessionActivity()` is the same activity clock the guard uses,
 * so renewal is still skipped while the user is idle, exactly as before.
 */
const OktaTokenRenewal: React.FC<OktaTokenRenewalProps> = ({
  children,
  renewalCheckInterval = 60000, // Check every minute
  renewBeforeExpiry = 300000, // Renew 5 minutes before expiry
  sessionRefreshInterval = 900000, // 15 minutes
}) => {
  const { oktaAuth, authState } = useOktaAuth()
  const { markActivity, isUserActive } = useSessionActivity()

  const lastSessionRefreshRef = useRef<number>(Date.now())
  const renewalTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isRenewingRef = useRef<boolean>(false)

  // Helper to detect unrecoverable OAuth errors (e.g., iframe renewal blocked by
  // third-party cookie policies)
  const isUnrecoverableOAuthError = useCallback((error: unknown): boolean => {
    const message = error instanceof Error ? error.message : String(error)
    return message.includes('not to prompt') || message.includes('login_required')
  }, [])

  const handleLogout = useCallback(async () => {
    // Through the same chokepoint as the interceptor's teardown (CAPA F274), so there is
    // exactly one place that can end a session and exactly one recording check. The idle
    // path should never reach here mid-encounter — SessionInactivityGuard refuses to warn
    // while a recording is active (F131) — but a control that depends on an upstream
    // guard staying correct is the shape that produced F273. This makes it structural.
    requestSessionTeardown(async () => {
      IS_DEV && console.log('[OktaTokenRenewal] Signing out of Okta...')
      try {
        await oktaAuth.signOut()
      } catch (error) {
        console.error('[OktaTokenRenewal] Error during logout:', error)
        // Force redirect to login page if signOut fails
        window.location.href = '/'
      }
    }, 'idle-countdown-expired-or-unrecoverable-okta-error')
  }, [oktaAuth])

  /**
   * Extend the Okta session. Renewal is skipped for the whole idle window
   * (refreshOktaSession/checkAndRenewTokens both bail when the user is inactive), so by
   * the time the warning dialog appears the access token may already be expired.
   * Without renewing here the dialog closes, the next API call 401s, and the interceptor
   * signs the user out anyway — the button appears to do nothing.
   */
  const handleExtend = useCallback(async () => {
    try {
      await oktaAuth.tokenManager.renew('accessToken')
      const idToken = await oktaAuth.tokenManager.get('idToken')
      if (idToken) {
        await oktaAuth.tokenManager.renew('idToken')
      }
      markActivity()
      lastSessionRefreshRef.current = Date.now()
      IS_DEV && console.log('[OktaTokenRenewal] Session extended')
    } catch (error) {
      console.error('[OktaTokenRenewal] Renewal failed:', error)
      if (isUnrecoverableOAuthError(error)) {
        // The session cannot be revived — send them to login rather than leaving them
        // on a dismissed warning and pretending they are still signed in.
        await handleLogout()
      }
      // Recoverable: activity is reset, so the periodic loop MAY retry — but
      // checkAndRenewTokens only renews while `timeUntilExpiry > 0`, so an
      // already-expired access token is never picked up there. Widening that guard is
      // a separate change.
    }
  }, [oktaAuth, markActivity, isUnrecoverableOAuthError, handleLogout])

  // Publish the Okta adapter to the root-mounted SessionInactivityGuard.
  useRegisterSessionAuth({
    isAuthenticated: !!authState?.isAuthenticated,
    extend: handleExtend,
    logout: handleLogout,
    label: 'okta',
  })

  // Keep the authentication active by periodically renewing tokens.
  // Note: oktaAuth.session.exists() returns 404 for OIDC flows with IDP discovery,
  // so we rely on token renewal instead.
  const refreshOktaSession = useCallback(async () => {
    if (!authState?.isAuthenticated) {
      return
    }
    if (!isUserActive()) {
      IS_DEV && console.warn('[OktaTokenRenewal] User INACTIVE - skipping session refresh')
      return
    }
    const timeSinceLastRefresh = Date.now() - lastSessionRefreshRef.current
    if (timeSinceLastRefresh < sessionRefreshInterval) {
      return
    }
    try {
      const accessToken = await oktaAuth.tokenManager.get('accessToken')
      if (accessToken) {
        IS_DEV && console.log('[OktaTokenRenewal] Refreshing Okta session...')
        await oktaAuth.tokenManager.renew('accessToken')
        lastSessionRefreshRef.current = Date.now()
        IS_DEV && console.log('[OktaTokenRenewal] Session refresh successful')
      }
    } catch (error) {
      console.error('Error refreshing Okta tokens:', error)
      if (isUnrecoverableOAuthError(error)) {
        IS_DEV && console.warn('[OktaTokenRenewal] Unrecoverable refresh error — redirecting to login')
        handleLogout()
      }
    }
  }, [
    authState?.isAuthenticated,
    oktaAuth,
    sessionRefreshInterval,
    isUserActive,
    isUnrecoverableOAuthError,
    handleLogout,
  ])

  // Renew tokens if they're expiring soon
  const checkAndRenewTokens = useCallback(async () => {
    if (!authState?.isAuthenticated || isRenewingRef.current) {
      return
    }
    if (!isUserActive()) {
      IS_DEV && console.warn('[OktaTokenRenewal] User INACTIVE - skipping token renewal check')
      return
    }

    try {
      const accessToken = await oktaAuth.tokenManager.get('accessToken')
      if (!accessToken) {
        return
      }
      const expiresAt = accessToken.expiresAt
      if (!expiresAt) {
        return
      }

      // expiresAt is in seconds
      const timeUntilExpiry = expiresAt * 1000 - Date.now()

      if (timeUntilExpiry <= renewBeforeExpiry && timeUntilExpiry > 0) {
        isRenewingRef.current = true
        IS_DEV &&
          console.log(
            `[OktaTokenRenewal] Token expiring in ${formatTime(timeUntilExpiry)} - renewing proactively...`
          )
        try {
          await oktaAuth.tokenManager.renew('accessToken')
          IS_DEV && console.log('[OktaTokenRenewal] Access token renewed successfully')

          const idToken = await oktaAuth.tokenManager.get('idToken')
          if (idToken) {
            await oktaAuth.tokenManager.renew('idToken')
            IS_DEV && console.log('[OktaTokenRenewal] ID token renewed successfully')
          }
        } catch (renewError) {
          console.error('[OktaTokenRenewal] Token renewal failed:', renewError)
          if (isUnrecoverableOAuthError(renewError)) {
            IS_DEV && console.warn('[OktaTokenRenewal] Unrecoverable renewal error — redirecting to login')
            isRenewingRef.current = false
            handleLogout()
            return
          }
        } finally {
          isRenewingRef.current = false
        }
      }
    } catch (error) {
      console.error('Error checking token expiry:', error)
      isRenewingRef.current = false
    }
  }, [
    authState?.isAuthenticated,
    oktaAuth,
    renewBeforeExpiry,
    isUserActive,
    isUnrecoverableOAuthError,
    handleLogout,
  ])

  // Set up token renewal check interval
  useEffect(() => {
    if (!authState?.isAuthenticated) {
      return
    }
    checkAndRenewTokens()
    refreshOktaSession()

    renewalTimerRef.current = setInterval(() => {
      checkAndRenewTokens()
      refreshOktaSession()
    }, renewalCheckInterval)

    return () => {
      if (renewalTimerRef.current) {
        clearInterval(renewalTimerRef.current)
        renewalTimerRef.current = null
      }
    }
  }, [authState?.isAuthenticated, checkAndRenewTokens, refreshOktaSession, renewalCheckInterval])

  // Listen for Okta token events
  useEffect(() => {
    const handleExpired = (key: string) => {
      IS_DEV && console.warn(`Okta token expired: ${key}`)
      // Token expired - Okta will handle redirect to login
    }

    const handleRenewed = (_key: string) => {
      isRenewingRef.current = false
      // The backend-minted JWE wraps the access token we just replaced. Okta will
      // introspect that token as active=false, so every request still carrying the
      // cached JWE comes back 401 — which the interceptor used to treat as session
      // death, dropping the clinician to the login page with no countdown dialog.
      // Evict here, at the event that makes it stale; the next request re-mints.
      clearBackendToken()
    }

    const handleError = (error: Error) => {
      console.error('Okta token manager error:', error)
      isRenewingRef.current = false
      if (isUnrecoverableOAuthError(error)) {
        IS_DEV && console.warn('[OktaTokenRenewal] Unrecoverable token manager error — redirecting to login')
        handleLogout()
      }
    }

    oktaAuth.tokenManager.on('expired', handleExpired)
    oktaAuth.tokenManager.on('renewed', handleRenewed)
    oktaAuth.tokenManager.on('error', handleError)

    return () => {
      oktaAuth.tokenManager.off('expired', handleExpired)
      oktaAuth.tokenManager.off('renewed', handleRenewed)
      oktaAuth.tokenManager.off('error', handleError)
    }
  }, [oktaAuth, isUnrecoverableOAuthError, handleLogout])

  return <>{children}</>
}

export default OktaTokenRenewal
